/**
 * NPEFixationFormDialog — Create/Edit NPE Fixation (Access form
 * `frmPurchaseQryNPEFixationAdd`). Layout phẳng 1:1 với form Access gồm 3 section:
 *   1. NPEs - Information      (read-only NPE info + alert allocations)
 *   2. Fixation - Information  (cs_fixation_id, pl_index, fixation_no, date,
 *                              quantity, price, note)
 *   3. Fixation - P Contract   (toàn bộ field LocalP companion)
 *
 * Workflow (theo VBA):
 *   - btnNew_Click  → mở dialog mode='create' (contract_no/p_contract_no enable).
 *   - btnEdit_Click → mở dialog mode='edit'   (contract_no/p_contract_no/pl_index
 *                     disable; pre-fill si/sd/fixed_fx/provi_market_rate/
 *                     stoploss_rate/trader_in_charge_name/intermediary…flags).
 *   - Khi chọn NPE no (`contract_no_AfterUpdate` → check_money):
 *       quantity_kg=to_be_fix, p_cp_code, p_quality_code, p_certification,
 *       p_package_code, p_delivery_place, p_latest_delivery, p_shipper_code,
 *       p_price_unit='VND/KG', p_contract_type='Outright', auto p_no_of_bag.
 *   - `price_AfterUpdate` / `quantity_kg_AfterUpdate` / `p_package_code_AfterUpdate`
 *       → tính lại p_no_of_bag = round(quantity_kg / package_size hoặc /60).
 *   - 2 nút Save:
 *       * "Save"             → POST /create  (cs_fixation + local_p)
 *       * "Save & Allocate"  → POST /create-and-allocate → navigate
 *         /purchase/npe-fixations/{id}/allocate?session={uid}.
 *         (Ẩn khi đã có allocations.)
 */
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { TextFieldProps } from '@mui/material/TextField'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { HIDE_ON_MOBILE_INLINE } from '@/theme/responsive'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  numSx,
  roSx,
} from './dialogStyle'

import { usePagePermission } from '@/hooks/usePagePermission'
import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useListCertificationsQuery,
  useListDeliveryPlacesQuery,
  useListDeliveryTermsQuery,
  useListFixationCallsQuery,
  useListFuturesMonthsQuery,
  useListLicensesQuery,
  useListMeOrIbQuery,
  useListPackagesQuery,
  useListPriceUnitsQuery,
  useListQualitiesQuery,
  useListTradersQuery,
  useLookupVendorsQuery,
} from '@/store/api/lookupsApi'
import {
  useGetLocalPQuery,
  useLazyCheckLocalPContractNoExistsQuery,
  useLazySuggestLocalPContractNoQuery,
  useSuggestLocalPContractNoQuery,
} from '@/store/api/localPApi'
import { useGetNPEContractDetailsQuery, useListNPEContractsQuery } from '@/store/api/npeContractsApi'
import {
  useCreateAndAllocateNPEFixationMutation,
  useCreateNPEFixationMutation,
  useSuggestNPEFixationNoQuery,
  useUpdateNPEFixationMutation,
} from '@/store/api/npeFixationsApi'
import type {
  NPEContractOut,
  NPEFixationCreateAndAllocateOut,
  NPEFixationCreateIn,
  NPEFixationOut,
} from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { EMPTY_LOCALP, type LocalPFormValues } from './localp-form-tabs/types'

// Access "Standard" format = #,##0.00 — đồng nhất chuẩn hiển thị số trong
// Purchase Contract dialogs (KG, VND/KG, %, ...).
const fmtStandard = (v: number | null | undefined): string =>
  v == null || Number.isNaN(v)
    ? ''
    : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Merged form values: fixation header + tất cả field LocalPFormValues. */
interface NPEFixationFormValues extends LocalPFormValues {
  // Fixation header (cs_fixation)
  npe_no: string
  fixation_date: string
  fixation_price: number | ''
  fixation_note: string
}

const EMPTY_FIX: NPEFixationFormValues = {
  ...EMPTY_LOCALP,
  // Fixation đã chốt giá → luôn Outright (Access mặc định)
  contract_type: 'Outright',
  npe_no: '',
  fixation_date: dayjs().format('YYYY-MM-DD'),
  fixation_price: '',
  fixation_note: '',
}

// Schema chỉ giữ type-shape — KHÔNG validate required/positive ở đây.
// Access btnSave_Click (form_frmPurchaseQryNPEFixationAdd.txt:6911-6980) dùng
// chuỗi ElseIf MsgBox để báo lỗi từng field theo thứ tự, KHÔNG có cơ chế
// "highlight". Toàn bộ validation 1:1 Access nằm trong `validateAccessChain`
// bên dưới. Yup ở đây chỉ ép kiểu để TypeScript hài lòng.
const schema: yup.ObjectSchema<NPEFixationFormValues> = yup.object({
  // ---- Fixation header ----
  npe_no: yup.string().trim().default(''),
  fixation_date: yup.string().default(''),
  fixation_price: yup.mixed<number | ''>().default(''),
  fixation_note: yup.string().default(''),

  // ---- LocalP ----
  contract_no: yup.string().trim().default('').max(20),
  contract_date: yup.string().default(''),
  cp_code: yup.string().trim().default(''),
  seller_ref: yup.string().trim().default(''),
  quality_code: yup.string().trim().default(''),
  quality_des_contract: yup.string().trim().default(''),
  certification: yup.string().trim().default(''),
  me_or_ib: yup.mixed<'ME' | 'IB' | ''>().oneOf(['ME', 'IB', ''] as const).default(''),
  license_cert_no: yup.string().trim().default(''),
  quantity_kg: yup.mixed<number | ''>().default(''),
  no_of_bag: yup.mixed<number | ''>().default(''),
  package: yup.string().trim().default(''),

  contract_type: yup
    .mixed<'Outright' | 'Differential'>()
    .oneOf(['Outright', 'Differential'] as const)
    .default('Outright'),
  outright_price: yup.mixed<number | ''>().default(''),
  ptbf_diffs: yup.mixed<number | ''>().default(''),
  price_unit: yup.string().trim().default(''),
  vat_rate: yup.mixed<number | ''>().default(''),
  fx: yup.mixed<number | ''>().default(''),
  fixed_fx: yup.mixed<number | ''>().default(''),
  futures_month: yup.string().trim().default(''),
  fixation_call: yup.string().trim().default(''),

  latest_delivery: yup.string().default(''),
  delivery_term: yup.string().trim().default(''),
  delivery_place_code: yup.string().trim().default(''),
  logistic_cost: yup.mixed<number | ''>().default(''),
  trader_in_charge: yup.string().trim().default(''),

  provi_market_rate: yup.mixed<number | ''>().default(''),
  stoploss_rate: yup.mixed<number | ''>().default(''),
  margin_value_per: yup.mixed<number | ''>().default(''),
  margin_limit_per: yup.mixed<number | ''>().default(''),
  time_limit: yup.mixed<number | ''>().default(''),
  margin_time_limit: yup.string().trim().default(''),
  margin_bonus_per: yup.mixed<number | ''>().default(''),
  margin_interest_month: yup.mixed<number | ''>().default(''),
  deposit_repayment_date: yup.string().default(''),

  is_intermediary: yup.boolean().default(false),
  shipper_code: yup.string().trim().default(''),
  contract_shipper_intermediary: yup.string().trim().default(''),
  outright_shipper_intermediary: yup.mixed<number | ''>().default(''),
  ptbfdiffs_shipper_intermediary: yup.mixed<number | ''>().default(''),

  is_issued: yup.boolean().default(false),
  is_sent_mail: yup.boolean().default(false),
  is_received_mail: yup.boolean().default(false),
  is_sent_paper: yup.boolean().default(false),
  is_received_paper: yup.boolean().default(false),
  applicable_vat: yup.boolean().default(false),
  note: yup.string().trim().default(''),
}) as yup.ObjectSchema<NPEFixationFormValues>

/** Build payload FLAT theo BE schema NPEFixationCreateIn. */
function valuesToCreateIn(v: NPEFixationFormValues): NPEFixationCreateIn {
  const num = (x: number | ''): number | null => (x === '' ? null : x)
  const str = (x: string): string | null => (x.trim() === '' ? null : x.trim())
  return {
    // Fixation header
    npe_no: v.npe_no.trim(),
    fixation_date: v.fixation_date,
    fixation_price: typeof v.fixation_price === 'number' ? v.fixation_price : 0,
    fixation_quantity: typeof v.quantity_kg === 'number' ? v.quantity_kg : null,
    note: v.fixation_note.trim() || null,

    // LocalP companion
    contract_no: str(v.contract_no),
    contract_date: v.contract_date || null,
    cp_code: v.cp_code.trim() || null,
    quality_code: str(v.quality_code),
    certification: str(v.certification),
    package_code: str(v.package),
    delivery_place_code: str(v.delivery_place_code),
    latest_delivery: str(v.latest_delivery),
    shipper_code: str(v.shipper_code),
    trader_in_charge: str(v.trader_in_charge),

    seller_ref: str(v.seller_ref),
    quantity: typeof v.quantity_kg === 'number' ? v.quantity_kg : null,
    no_of_bag: num(v.no_of_bag),
    me_or_ib: v.me_or_ib === '' ? null : v.me_or_ib,
    license_cert_no: str(v.license_cert_no),

    price_unit: str(v.price_unit),
    futures_month: str(v.futures_month),
    fixation_call: str(v.fixation_call),
    fx: num(v.fx),
    vat_rate: num(v.vat_rate),
    applicable_vat: v.applicable_vat ?? null,

    delivery_term: str(v.delivery_term),
    logistic_cost: num(v.logistic_cost),

    fixed_fx: num(v.fixed_fx),
    provi_market_rate: num(v.provi_market_rate),
    stoploss_rate: num(v.stoploss_rate),

    intermediary: v.is_intermediary,
    shipper_contract_ref: str(v.contract_shipper_intermediary),
    shipper_outright_price: num(v.outright_shipper_intermediary),
    shipper_ptbf_diffs: num(v.ptbfdiffs_shipper_intermediary),

    is_issued: v.is_issued,
    is_sent_mail: v.is_sent_mail,
    is_received_mail: v.is_received_mail,
    is_sent_paper: v.is_sent_paper,
    is_received_paper: v.is_received_paper,

    p_note: str(v.note),
  }
}

/**
 * Auto-fill LocalP từ NPE — bám sát `check_money` Access (line 7083-7127).
 *
 * Access pull những field này từ NPE qua DLookup `public_v_consignment_p`:
 *   - p_cp_code, p_shipper_code, p_quality_code, p_certification,
 *     p_package_code, p_delivery_place, p_latest_delivery (= delivery_time)
 *   - quantity_kg = to_be_fix
 *   - p_price_unit = "VND/KG", p_contract_type = "Outright"
 *
 * Access KHÔNG pull (user phải tự nhập mỗi fixation):
 *   - stoploss_rate, provi_market_rate, fixed_fx, trader_in_charge
 *   - vat_rate / applicable_vat (default 0)
 *   - is_intermediary (default 0 — checkbox độc lập, không tự check theo shipper)
 *
 * Memory `feedback_access_docs_strict`: bám đúng spec, không tự thêm logic.
 */
function applyNPEAutofill(
  setValue: ReturnType<typeof useForm<NPEFixationFormValues>>['setValue'],
  npe: NPEContractOut,
) {
  const set = (k: keyof NPEFixationFormValues, val: unknown) => {
    // @ts-expect-error setValue signature with dynamic key
    setValue(k, val ?? '', { shouldDirty: true })
  }
  set('cp_code', npe.cp_code ?? '')
  set('quality_code', npe.quality_code ?? '')
  set('certification', npe.certification ?? '')
  set('package', npe.package_code ?? '')
  set('latest_delivery', npe.delivery_time ?? '')
  set('delivery_place_code', npe.delivery_place_code ?? '')
  set('shipper_code', npe.shipper_code ?? '')
  set('quantity_kg', npe.to_be_fix ?? npe.quantity_kg ?? '')
  set('price_unit', 'VND/KG')
  set('contract_type', 'Outright')
}

/**
 * Access `check_money` ELSE branch (line 7112-7124) — khi user clear `contract_no`,
 * reset các field auto-fill từ NPE.
 */
function clearNPEAutofill(
  setValue: ReturnType<typeof useForm<NPEFixationFormValues>>['setValue'],
) {
  const set = (k: keyof NPEFixationFormValues, val: unknown) => {
    // @ts-expect-error setValue signature with dynamic key
    setValue(k, val, { shouldDirty: true })
  }
  set('cp_code', '')
  set('quality_code', '')
  set('certification', '')
  set('shipper_code', '')
  set('quantity_kg', '')
  set('no_of_bag', '')
  set('price_unit', 'VND/KG')
  set('contract_type', 'Outright')
}

/**
 * Numeric input — Access Format="Standard"/"Percent": hiển thị thousand-separator
 * khi blur, cho user gõ raw khi focus (tránh stuck cursor khi format mid-typing).
 *
 *   - `decimals`: số chữ số thập phân tối đa (vd KG=0, price=2).
 *   - `percent`: true = Access `Format="Percent"`. Storage là decimal (0.7),
 *     display ×100 với suffix `%` (chị thấy 70, lưu 0.7). Khớp với
 *     `formatAccessPercent` trong LocalPListPage.
 *   - `value`: `number | ''`. onChange callback nhận đúng kiểu này.
 *   - Strip dấu phẩy lúc parse → store là plain number.
 */
type NumericFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value: number | ''
  onChange: (v: number | '') => void
  decimals?: number
  percent?: boolean
}
function NumericField({
  value,
  onChange,
  decimals = 0,
  percent = false,
  inputProps,
  InputProps,
  onFocus,
  onBlur,
  ...rest
}: NumericFieldProps) {
  const [focused, setFocused] = useState(false)
  const scaled =
    value === '' || value == null
      ? ''
      : percent
        ? Number((value * 100).toFixed(decimals > 0 ? decimals : 6))
        : value
  const display =
    scaled === ''
      ? ''
      : focused
        ? String(scaled)
        : (scaled as number).toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: percent ? Math.max(decimals, 2) : decimals,
          })
  return (
    <TextField
      {...rest}
      type="text"
      value={display}
      inputProps={{
        inputMode: decimals > 0 || percent ? 'decimal' : 'numeric',
        ...inputProps,
      }}
      InputProps={{
        endAdornment: percent ? (
          <InputAdornment position="end">%</InputAdornment>
        ) : InputProps?.endAdornment,
        ...InputProps,
      }}
      onFocus={(e) => {
        setFocused(true)
        onFocus?.(e)
      }}
      onBlur={(e) => {
        setFocused(false)
        onBlur?.(e)
      }}
      onChange={(e) => {
        // Cho phép thousand separator user paste vào → strip trước khi parse.
        const raw = e.target.value.replace(/[,\s%]/g, '')
        if (raw === '' || raw === '-' || raw === '.') {
          onChange('')
          return
        }
        const n = Number(raw)
        if (!Number.isFinite(n)) return
        onChange(percent ? n / 100 : n)
      }}
    />
  )
}

// ---- Section heading helper ----
function SectionHeader({ title }: { title: string }) {
  return (
    <Box
      sx={{
        bgcolor: 'action.hover',
        px: 1,
        py: 0.5,
        mt: 1,
        borderLeft: 3,
        borderColor: 'primary.main',
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
        {title}
      </Typography>
    </Box>
  )
}

export interface NPEFixationFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: NPEFixationOut | null
  /** Khi mở từ NPE detail tab, lock npe_no. */
  defaultNpeContractNo?: string
  onClose: () => void
  onSaved?: (id: number) => void
  /** Access `btnSaveAllocate_Click`: sau khi save thành công, parent mở
   *  NPEAllocationAddDialog với fixation + session_uid mới tạo. */
  onSaveAllocate?: (out: NPEFixationCreateAndAllocateOut) => void
}

export function NPEFixationFormDialog({
  open,
  mode,
  initialValue,
  defaultNpeContractNo,
  onClose,
  onSaved,
  onSaveAllocate,
}: NPEFixationFormDialogProps) {
  const snack = useSnackbar()
  const { canEdit } = usePagePermission()
  const isEdit = mode === 'edit'

  const [createFix, { isLoading: creating }] = useCreateNPEFixationMutation()
  const [createAndAllocate, { isLoading: allocating }] =
    useCreateAndAllocateNPEFixationMutation()
  const [updateFix, { isLoading: updating }] = useUpdateNPEFixationMutation()
  const loading = creating || allocating || updating

  // ---- Lookups ----
  // List query phục vụ combo options (intentionally date-filtered theo Purchase
  // header user_preferences — user pick từ scope hiện hành).
  const { data: npeList } = useListNPEContractsQuery({ page: 1, page_size: 1_000_000 })
  const npeOptions = npeList?.items ?? []

  const { data: vendors = [] } = useLookupVendorsQuery({ q: '' })
  const { data: qualities = [] } = useListQualitiesQuery({ local_only: true })
  const { data: certifications = [] } = useListCertificationsQuery()
  const { data: meOrIbOpts = [] } = useListMeOrIbQuery()
  const { data: packages = [] } = useListPackagesQuery()
  const { data: priceUnits = [] } = useListPriceUnitsQuery()
  const { data: futuresMonths = [] } = useListFuturesMonthsQuery()
  const { data: fixationCalls = [] } = useListFixationCallsQuery()
  const { data: deliveryTerms = [] } = useListDeliveryTermsQuery()
  const { data: deliveryPlaces = [] } = useListDeliveryPlacesQuery()
  const { data: traders = [] } = useListTradersQuery()

  // Form
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<NPEFixationFormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY_FIX,
  })

  const npeNo = watch('npe_no')
  const cpCode = watch('cp_code')
  const fixationPrice = watch('fixation_price')
  const applicableVat = watch('applicable_vat')
  const currentPackage = watch('package')
  const currentQty = watch('quantity_kg')
  const licenseCertNo = watch('license_cert_no')
  const traderInCharge = watch('trader_in_charge')
  const shipperCode = watch('shipper_code')
  const deliveryPlaceCode = watch('delivery_place_code')
  const siValue = watch('si') as number | ''
  const sdValue = watch('sd') as number | ''

  // Licenses (cần cp_code để filter)
  const { data: licenses = [] } = useListLicensesQuery(
    cpCode ? { cp_code: cpCode } : undefined,
    { skip: !cpCode },
  )

  // Vendor lookup BE giới hạn `limit=20` (lookups.py:410). Nếu NPE.cp_code
  // không nằm trong top 20 thì `selectedVendor` sẽ null → "Supplier Name" trống.
  // Truy vấn riêng theo `q = cpCode` để luôn có name khớp NPE.
  const { data: vendorsForCp = [] } = useLookupVendorsQuery(
    cpCode ? { q: cpCode } : undefined,
    { skip: !cpCode },
  )
  const { data: vendorsForShipper = [] } = useLookupVendorsQuery(
    shipperCode ? { q: shipperCode } : undefined,
    { skip: !shipperCode },
  )

  // ---- Derived ----
  // Autofill `supplier_name` + `to_be_fix` lấy từ single-row detail endpoint
  // BYPASS date filter (cùng pattern Futures Allocation). Trước đây dùng
  // `npeOptions.find()` ⇒ NPE ngoài Purchase date range → null → "Supplier
  // Name" trống, validate `qty <= to_be_fix` sai. `keepUnusedDataFor: 0` +
  // `refetchOnMountOrArgChange` ⇒ fresh mỗi lần đổi npe_no.
  const npeNoTrim = (npeNo ?? '').trim()
  const npeDetailQ = useGetNPEContractDetailsQuery(npeNoTrim, {
    skip: !open || !npeNoTrim,
    refetchOnMountOrArgChange: true,
  })
  const selectedNpe = useMemo(() => {
    if (!npeNoTrim) return null
    const detail = npeDetailQ.data?.data
    if (detail && detail.contract_no === npeNoTrim) return detail
    // Fallback list cache để tránh nhấp nháy 1 frame khi detail còn fetching.
    return npeOptions.find((c) => c.contract_no === npeNoTrim) ?? null
  }, [npeNoTrim, npeDetailQ.data, npeOptions])
  const selectedVendor = useMemo(() => {
    if (!cpCode) return null
    return (
      vendorsForCp.find((v) => v.code === cpCode) ??
      vendors.find((v) => v.code === cpCode) ??
      null
    )
  }, [cpCode, vendors, vendorsForCp])
  const selectedPackage = useMemo(
    () => packages.find((p) => p.value === currentPackage) ?? null,
    [packages, currentPackage],
  )
  const selectedShipper = useMemo(() => {
    if (!shipperCode) return null
    return (
      vendorsForShipper.find((v) => v.code === shipperCode) ??
      vendors.find((v) => v.code === shipperCode) ??
      null
    )
  }, [shipperCode, vendors, vendorsForShipper])
  const selectedDeliveryPlace = useMemo(
    () => deliveryPlaces.find((d) => d.value === deliveryPlaceCode) ?? null,
    [deliveryPlaces, deliveryPlaceCode],
  )

  // Access `btnNew_Click` (line 965 subform): `p_contract_no = Suggest_IndexName("local_p")`.
  // Gọi BE /purchase/local-p/suggest-contract-no khi mở form Add → fill ngay
  // P-XXXXX cho field "Contract No." (LocalP). User vẫn override được.
  // refetchOnMountOrArgChange để mỗi lần mở Add đều lấy P-XXXXX mới nhất.
  const skipSuggestP = isEdit || !open
  const { data: suggestPRes } = useSuggestLocalPContractNoQuery(undefined, {
    skip: skipSuggestP,
    refetchOnMountOrArgChange: true,
  })

  // Annex Index + Fixation No. (Access `Suggest_IndexName("phulucindex"/"phuluc" + NPE no)`).
  // Create mode: gọi BE /purchase/npe-fixations/suggest — query `MAX(pl_index)+1`
  // từ `v_phuluc_view` (parity 1:1 với Access, không dùng COUNT(*) từ cs_fixation).
  // Edit mode: parse trực tiếp từ initialValue.fixation_no.
  const skipSuggest = isEdit || !open || !npeNo
  const { data: suggestRes } = useSuggestNPEFixationNoQuery(npeNo, { skip: skipSuggest })

  const annexIndexLabel = useMemo(() => {
    if (isEdit && initialValue?.fixation_no) {
      // Access label "Annex Index" = số nguyên, không prefix PL → strip nếu có.
      const m = /\/PL(\d+)$/i.exec(initialValue.fixation_no)
      if (m) return m[1]
      if (typeof initialValue.pl_index === 'number') {
        return String(initialValue.pl_index).padStart(2, '0')
      }
    }
    if (!npeNo) return ''
    return suggestRes?.data?.annex_index ?? ''
  }, [isEdit, initialValue, npeNo, suggestRes])

  const fixationNoPreview = useMemo(() => {
    if (isEdit && initialValue?.fixation_no) return initialValue.fixation_no
    if (!npeNo) return ''
    return suggestRes?.data?.fixation_no ?? ''
  }, [isEdit, initialValue, npeNo, suggestRes])

  const allocationsCount = isEdit ? initialValue?.allocations_count ?? 0 : 0

  // Reset khi open hoặc đổi record
  useEffect(() => {
    if (!open) return
    if (isEdit && initialValue) {
      reset({
        ...EMPTY_FIX,
        npe_no: initialValue.contract_no ?? '',
        fixation_date: initialValue.fixation_date ?? dayjs().format('YYYY-MM-DD'),
        fixation_price: initialValue.price ?? '',
        fixation_note: initialValue.note ?? '',
        contract_no: initialValue.p_contract_no ?? '',
        contract_date: initialValue.p_contract_date ?? dayjs().format('YYYY-MM-DD'),
        cp_code: initialValue.cp_code ?? '',
        quality_code: initialValue.quality_code ?? '',
        certification: initialValue.p_certification ?? initialValue.certification ?? '',
        // `local_p.me_or_ib` là CHAR(50) → padded space. Trim + chỉ accept
        // 'ME'/'IB' để MUI Select match đúng MenuItem; mọi giá trị khác → ''.
        me_or_ib: ((): 'ME' | 'IB' | '' => {
          const raw = (initialValue.p_me_or_ib ?? '').trim()
          return raw === 'ME' || raw === 'IB' ? raw : ''
        })(),
        license_cert_no: initialValue.p_license_cert_no ?? '',
        quantity_kg: initialValue.quantity_kg ?? '',
        no_of_bag: initialValue.p_no_of_bag ?? '',
        package: initialValue.p_package_code ?? '',
        contract_type:
          (initialValue.p_contract_type as 'Outright' | 'Differential' | null) ??
          'Outright',
        outright_price: initialValue.p_outright_price ?? '',
        price_unit: initialValue.p_price_unit ?? 'VND/KG',
        futures_month: initialValue.p_futures_month ?? '',
        fixation_call: initialValue.p_fixation_call ?? '',
        fx: initialValue.p_fx ?? '',
        logistic_cost: initialValue.p_logistic_cost ?? '',
        latest_delivery: initialValue.p_latest_delivery ?? '',
        delivery_term: initialValue.p_delivery_term ?? '',
        delivery_place_code: initialValue.p_delivery_place ?? initialValue.delivery_place_code ?? '',
        note: initialValue.p_note ?? '',
        shipper_code: initialValue.p_shipper_code ?? '',
      })
    } else {
      reset({
        ...EMPTY_FIX,
        npe_no: defaultNpeContractNo ?? '',
      })
    }
  }, [open, isEdit, initialValue, defaultNpeContractNo, reset])

  // Edit mode: NPEFixationOut từ view `v_cs_fixation_p` không kèm các field
  // user-input bên local_p (Access comment block trên `applyNPEAutofill`:
  // trader_in_charge, vat_rate/applicable_vat, provi_market_rate, stoploss_rate,
  // fixed_fx, is_intermediary, si, sd...). Fetch detail local_p companion để
  // populate lại các field này — nếu không form Edit sẽ hiển thị rỗng.
  const editPContractNo = isEdit ? (initialValue?.p_contract_no ?? '') : ''
  const { data: editLocalPRes } = useGetLocalPQuery(editPContractNo, {
    skip: !open || !isEdit || !editPContractNo,
  })
  const editLocalP = editLocalPRes?.data
  useEffect(() => {
    if (!open || !isEdit || !editLocalP) return
    // Chỉ overwrite các field reset() ở trên chưa cover. Dùng shouldDirty=false
    // để các default reload không bị tính là "user đã sửa".
    const setRO = (k: keyof NPEFixationFormValues, v: unknown) => {
      // @ts-expect-error dynamic key
      setValue(k, v ?? '', { shouldDirty: false })
    }
    setRO('seller_ref', editLocalP.seller_ref ?? '')
    setRO('quality_des_contract', editLocalP.quality_des_contract ?? '')
    setRO('trader_in_charge', editLocalP.trader_in_charge ?? '')
    setRO('vat_rate', editLocalP.vat_rate ?? '')
    setValue('applicable_vat', !!editLocalP.applicable_vat, { shouldDirty: false })
    setRO('fixed_fx', editLocalP.fixed_fx ?? '')
    setRO('provi_market_rate', editLocalP.provi_market_rate ?? '')
    setRO('stoploss_rate', editLocalP.stoploss_rate ?? '')
    setValue('is_intermediary', !!editLocalP.is_intermediary, { shouldDirty: false })
    setRO('contract_shipper_intermediary', editLocalP.contract_shipper_intermediary ?? '')
    setRO('outright_shipper_intermediary', editLocalP.outright_shipper_intermediary ?? '')
    setRO('ptbfdiffs_shipper_intermediary', editLocalP.ptbfdiffs_shipper_intermediary ?? '')
    setValue('is_issued', !!editLocalP.is_issued, { shouldDirty: false })
    setValue('is_sent_mail', !!editLocalP.is_sent_mail, { shouldDirty: false })
    setValue('is_received_mail', !!editLocalP.is_received_mail, { shouldDirty: false })
    setValue('is_sent_paper', !!editLocalP.is_sent_paper, { shouldDirty: false })
    setValue('is_received_paper', !!editLocalP.is_received_paper, { shouldDirty: false })

    // "Load hết từ local_p": ngoài nhóm user-input ở trên, load nốt toàn bộ
    // field HĐ Purchase còn lại TỪ CHÍNH bản ghi local_p (nguồn chuẩn) thay vì
    // chỉ dựa view fixation — phòng trường hợp view không carry / lệch giá trị
    // đã lưu. KHÔNG đụng field thuộc fixation (npe_no, fixation_*, quantity_kg).
    setRO('contract_date', editLocalP.contract_date ?? '')
    setRO('cp_code', editLocalP.supplier_code ?? '') // supplier_code = local_p.cp_code
    setRO('quality_code', editLocalP.quality_code ?? '')
    setRO('certification', editLocalP.certification ?? '')
    setValue(
      'me_or_ib',
      ((): 'ME' | 'IB' | '' => {
        const raw = String(editLocalP.me_or_ib ?? '').trim()
        return raw === 'ME' || raw === 'IB' ? raw : ''
      })(),
      { shouldDirty: false },
    )
    setRO('license_cert_no', editLocalP.license_cert_no ?? '')
    setRO('no_of_bag', editLocalP.no_of_bag ?? '')
    setRO('package', editLocalP.package ?? '')
    setValue(
      'contract_type',
      (editLocalP.contract_type as 'Outright' | 'Differential' | null) ?? 'Outright',
      { shouldDirty: false },
    )
    setRO('outright_price', editLocalP.outright_price ?? '')
    setRO('price_unit', editLocalP.price_unit ?? '')
    setRO('futures_month', editLocalP.futures_month ?? '')
    setRO('fixation_call', editLocalP.fixation_call ?? '')
    setRO('fx', editLocalP.fx ?? '')
    setRO('latest_delivery', editLocalP.latest_delivery ?? '')
    setRO('delivery_term', editLocalP.delivery_term ?? '')
    setRO('delivery_place_code', editLocalP.delivery_place_code ?? '')
    setRO('logistic_cost', editLocalP.logistic_cost ?? '')
    setRO('shipper_code', editLocalP.shipper_code ?? '')
    setRO('note', editLocalP.note ?? '')
  }, [open, isEdit, editLocalP, setValue])

  // ---- Fill suggested p_contract_no khi mở Add (Access btnNew_Click line 965) ----
  // Chỉ fill 1 lần / lần mở; nếu user đã type rồi thì giữ giá trị user.
  const [appliedSuggestedP, setAppliedSuggestedP] = useState(false)
  useEffect(() => {
    if (isEdit || !open || appliedSuggestedP) return
    const suggested = suggestPRes?.contractNo
    if (!suggested) return
    const cur = (getValues('contract_no') ?? '').trim()
    if (cur === '') {
      setValue('contract_no', suggested, { shouldDirty: false })
    }
    setAppliedSuggestedP(true)
  }, [isEdit, open, appliedSuggestedP, suggestPRes, getValues, setValue])
  useEffect(() => {
    if (!open) setAppliedSuggestedP(false)
  }, [open])

  // ---- Pre-save check cho `contract_no` (skill access-suggest) ----
  // Access `contract_no_AfterUpdate` parity: khi user rời field, check
  // BE xem mã đã có active row chưa. Nếu trùng → suggest mã mới + điền
  // vào field + show inline Alert. KHÔNG auto-save.
  const [triggerCheckPContract] = useLazyCheckLocalPContractNoExistsQuery()
  const [triggerSuggestPContract, suggestPContractState] =
    useLazySuggestLocalPContractNoQuery()
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  // useRef mirror sync — chống race blur+click Save cùng tick (xem
  // skill access-suggest "Background — vì sao race xảy ra").
  const precheckBusyRef = useRef(false)

  useEffect(() => {
    if (!open) return
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
  }, [open, isEdit, initialValue?.cs_fixation_id])

  // Auto-dismiss alert khi user sửa contract_no khác mã suggest.
  const watchedContractNo = watch('contract_no')
  useEffect(() => {
    if (regenNotice && (watchedContractNo ?? '').trim() !== regenNotice.to) {
      setRegenNotice(null)
    }
  }, [watchedContractNo, regenNotice])

  const handleContractNoBlur = async () => {
    if (isEdit) return
    const val = (getValues('contract_no') ?? '').trim()
    if (!val) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheckPContract({ contractNo: val }).unwrap()
      if (!check.exists) return
      // preferCacheValue=false → force network refetch. `useSuggestLocalPContractNoQuery`
      // ở line 533 đã subscribe cache; nếu không force, lazy trigger trả mã
      // cũ (chính là mã đang trùng) → newVal === val → không update field.
      const next = await triggerSuggestPContract(undefined, false).unwrap()
      const newVal = (next.contractNo || '').trim()
      if (newVal && newVal !== val) {
        setValue('contract_no', newVal, { shouldDirty: true })
        setRegenNotice({ from: val, to: newVal })
      }
    } catch {
      // BE sẽ raise lúc Save nếu thật sự trùng — không poison UX.
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  // ---- Auto-fill / clear khi chọn / xoá NPE no (create mode) ----
  // Access `check_money` (contract_no_AfterUpdate / contract_no_Click).
  const [lastFilledNpe, setLastFilledNpe] = useState('')
  useEffect(() => {
    if (isEdit) return
    // Clear branch: user xoá NPE → reset các field auto-fill (Access ELSE branch).
    if (!npeNo) {
      if (lastFilledNpe) {
        clearNPEAutofill(setValue)
        setLastFilledNpe('')
      }
      return
    }
    if (npeNo === lastFilledNpe) return
    // Dùng `selectedNpe` (ưu tiên detail by-PK, bypass date filter) thay vì
    // `npeOptions.find()` để NPE ngoài range vẫn autofill được trong New mode.
    if (!selectedNpe || selectedNpe.contract_no !== npeNo) return
    applyNPEAutofill(setValue, selectedNpe)
    if (!getValues('contract_date')) {
      setValue('contract_date', dayjs().format('YYYY-MM-DD'))
    }
    setLastFilledNpe(npeNo)
  }, [npeNo, selectedNpe, lastFilledNpe, isEdit, setValue, getValues])

  // ---- Sync fixation_price → outright_price (Access price_AfterUpdate) ----
  useEffect(() => {
    if (typeof fixationPrice === 'number') {
      setValue('outright_price', fixationPrice, { shouldDirty: true })
    }
  }, [fixationPrice, setValue])

  // ---- Auto p_no_of_bag (Access p_package_code_AfterUpdate / quantity_kg_AfterUpdate /
  //      price_AfterUpdate đều gọi cùng logic).
  //   - package_code RỖNG hoặc quantity_kg RỖNG → no_of_bag = "".
  //   - nw_per_bag_kg > 0 → no_of_bag = ROUND(qty / nw_per_bag_kg).
  //   - nw_per_bag_kg rỗng/0 (Access `package_code.column(2)` empty/"0") → ROUND(qty / 60).
  // Lưu ý: option package từ `/lookups/packages/list` dùng field `nw_per_bag_kg`
  // (KHÔNG phải `package_size`/`size`) — đọc sai field trước đây khiến luôn /60.
  useEffect(() => {
    if (isEdit) return
    const hasQty = typeof currentQty === 'number' && currentQty > 0
    const hasPkg = !!currentPackage
    if (!hasQty || !hasPkg) {
      setValue('no_of_bag', '', { shouldDirty: true })
      return
    }
    const nwRaw = (selectedPackage as unknown as { nw_per_bag_kg?: number | string | null } | null)
      ?.nw_per_bag_kg
    const nw = Number(nwRaw)
    const size = Number.isFinite(nw) && nw > 0 ? nw : 60
    setValue('no_of_bag', Math.round((currentQty as number) / size), { shouldDirty: true })
  }, [currentQty, currentPackage, selectedPackage, isEdit, setValue])

  // ---- License change → set si/sd (p_license_cert_no_Change) ----
  useEffect(() => {
    const lic = licenses.find((l) => l.license_cert_no === licenseCertNo)
    if (lic) {
      setValue('si', lic.si ?? '', { shouldDirty: true })
      setValue('sd', lic.sd ?? '', { shouldDirty: true })
    }
  }, [licenseCertNo, licenses, setValue])

  // Reset state khi đóng
  useEffect(() => {
    if (!open) setLastFilledNpe('')
  }, [open])

  // ---- Save ----
  // Access `only_save` validation chain (line 6912-6981) — kiểm tra tuần tự
  // theo ElseIf, MsgBox lỗi đầu tiên. FE bám pattern `LocalPFormDialog`
  // (/purchase/contract/list): highlight đỏ TẤT CẢ field thiếu + snackbar
  // dùng message đầu tiên (giữ đúng thứ tự Access).
  type FieldKey = keyof NPEFixationFormValues
  type AccessIssue = { field: FieldKey; message: string }
  const validateAccessChain = (values: NPEFixationFormValues): AccessIssue[] => {
    const empty = (v: unknown) =>
      v === null || v === undefined || (typeof v === 'string' && v.trim() === '')
    const zero = (v: unknown) =>
      v === null || v === undefined || v === '' || (typeof v === 'number' && v === 0)
    const issues: AccessIssue[] = []

    // CS Fixation block — Access: pl_index không phải form field; map highlight
    // vào ô NPE Contract (khi chọn NPE, pl_index auto-derive).
    if (empty(annexIndexLabel)) issues.push({ field: 'npe_no', message: 'Please choose Annex Index!' })
    if (zero(values.fixation_price)) issues.push({ field: 'fixation_price', message: 'Please type Fixation Price!' })
    if (zero(values.quantity_kg)) issues.push({ field: 'quantity_kg', message: 'Please type Fixation Quantity!' })
    // Access: check_num = quantity_kg(current) khi edit; = 0 khi create.
    // Mục đích: cho phép edit giữ nguyên/giảm/tăng tới to_be_fix + current.
    const tbf = selectedNpe?.to_be_fix ?? null
    if (tbf != null && typeof values.quantity_kg === 'number') {
      const checkNum = isEdit ? (initialValue?.quantity_kg ?? 0) : 0
      if (values.quantity_kg > tbf + checkNum + 1e-9) {
        issues.push({ field: 'quantity_kg', message: 'Please type Fixation Quantity <= To be Fix!' })
      }
    }

    // LocalP block (Access lần lượt từng field).
    // p_contract_no: FE auto-gen P-XXXXX khi rỗng → bỏ check empty.
    if (empty(values.contract_date)) issues.push({ field: 'contract_date', message: 'Please choose Purchase Date!' })
    if (zero(values.quantity_kg)) issues.push({ field: 'quantity_kg', message: 'Please type P Quantity!' })
    if (zero(values.no_of_bag)) issues.push({ field: 'no_of_bag', message: 'Please type P no of bag!' })
    if (empty(values.certification)) issues.push({ field: 'certification', message: 'Please choose P Certification!' })
    if (zero(values.outright_price)) issues.push({ field: 'outright_price', message: 'Please choose P Outright Price!' })
    if (empty(values.package)) issues.push({ field: 'package', message: 'Please choose P Package!' })
    if (empty(values.delivery_term)) issues.push({ field: 'delivery_term', message: 'Please choose P Delivery Term!' })
    if (empty(values.delivery_place_code)) issues.push({ field: 'delivery_place_code', message: 'Please choose P Delivery Place!' })
    if (empty(values.price_unit)) issues.push({ field: 'price_unit', message: 'Please choose P Price Unit!' })
    if (empty(values.trader_in_charge)) issues.push({ field: 'trader_in_charge', message: 'Please choose Trader In Charge!' })
    if (zero(values.provi_market_rate)) issues.push({ field: 'provi_market_rate', message: 'Please type Provisional on Market Price!' })
    if (zero(values.stoploss_rate)) issues.push({ field: 'stoploss_rate', message: 'Please type Stoploss rate!' })
    if (values.applicable_vat && empty(values.vat_rate)) issues.push({ field: 'vat_rate', message: 'Please input VAT Rate!' })
    if (!values.applicable_vat && !empty(values.vat_rate)) issues.push({ field: 'vat_rate', message: 'Please do not input VAT Rate!' })
    return issues
  }

  const submitWithMode = (m: 'save' | 'save-allocate') =>
    handleSubmit(
      async (values) => {
        // Race guard: nếu user click Save khi field contract_no đang focus,
        // blur fire trước → handleContractNoBlur start (set ref true).
        // Click Save tới đây phải bỏ qua, đợi precheck resolve.
        if (precheckBusyRef.current) return
        clearErrors()
        const issues = validateAccessChain(values)
        if (issues.length > 0) {
          // Pattern khớp LocalPFormDialog (/purchase/contract/list): tô đỏ
          // tất cả field thiếu + snackbar text = MsgBox đầu tiên (Access order).
          const seen = new Set<FieldKey>()
          for (const it of issues) {
            if (seen.has(it.field)) continue
            seen.add(it.field)
            setError(it.field, { type: 'access', message: it.message })
          }
          snack.error(issues[0].message)
          return
        }
        const payload = valuesToCreateIn(values)
        try {
          if (isEdit && initialValue) {
            // Access btnSave_Click `only_save()` (form_frmPurchaseQryNPEFixationAdd.txt
            // line 6892-7049): UPDATE cả cs_fixation HEADER và local_p companion
            // trong cùng một save. BE NPEFixationUpdateIn đã mở rộng để nhận tất
            // cả field này (`_LP_FIELD_MAP` trong service tách field ra local_p).
            const res = await updateFix({
              id: initialValue.cs_fixation_id,
              body: {
                // cs_fixation header
                fixation_date: values.fixation_date,
                quantity_kg:
                  typeof values.quantity_kg === 'number' ? values.quantity_kg : null,
                price:
                  typeof values.fixation_price === 'number' ? values.fixation_price : null,
                note: values.fixation_note.trim() || null,
                // local_p companion — gửi đầy đủ field user nhập / auto-fill
                contract_date: values.contract_date || null,
                cp_code: values.cp_code.trim() || null,
                quality_code: values.quality_code.trim() || null,
                certification: values.certification.trim() || null,
                package_code: values.package.trim() || null,
                delivery_place_code: values.delivery_place_code.trim() || null,
                latest_delivery: values.latest_delivery || null,
                shipper_code: values.shipper_code.trim() || null,
                trader_in_charge: values.trader_in_charge.trim() || null,
                seller_ref: values.seller_ref.trim() || null,
                no_of_bag:
                  typeof values.no_of_bag === 'number' ? values.no_of_bag : null,
                me_or_ib: values.me_or_ib === '' ? null : values.me_or_ib,
                license_cert_no: values.license_cert_no.trim() || null,
                price_unit: values.price_unit.trim() || null,
                futures_month: values.futures_month.trim() || null,
                fixation_call: values.fixation_call.trim() || null,
                fx: typeof values.fx === 'number' ? values.fx : null,
                vat_rate:
                  typeof values.vat_rate === 'number' ? values.vat_rate : null,
                applicable_vat: values.applicable_vat ?? null,
                delivery_term: values.delivery_term.trim() || null,
                logistic_cost:
                  typeof values.logistic_cost === 'number' ? values.logistic_cost : null,
                fixed_fx:
                  typeof values.fixed_fx === 'number' ? values.fixed_fx : null,
                provi_market_rate:
                  typeof values.provi_market_rate === 'number'
                    ? values.provi_market_rate
                    : null,
                stoploss_rate:
                  typeof values.stoploss_rate === 'number' ? values.stoploss_rate : null,
                intermediary: values.is_intermediary,
                shipper_contract_ref:
                  values.contract_shipper_intermediary.trim() || null,
                shipper_outright_price:
                  typeof values.outright_shipper_intermediary === 'number'
                    ? values.outright_shipper_intermediary
                    : null,
                shipper_ptbf_diffs:
                  typeof values.ptbfdiffs_shipper_intermediary === 'number'
                    ? values.ptbfdiffs_shipper_intermediary
                    : null,
                is_issued: values.is_issued,
                is_sent_mail: values.is_sent_mail,
                is_received_mail: values.is_received_mail,
                is_sent_paper: values.is_sent_paper,
                is_received_paper: values.is_received_paper,
                p_note: values.note.trim() || null,
              },
            }).unwrap()
            snack.success(`Updated Fixation ${initialValue.fixation_no}`)
            onSaved?.(res.data?.cs_fixation_id ?? initialValue.cs_fixation_id)
            onClose()
            return
          }
          if (m === 'save-allocate') {
            const res = await createAndAllocate(payload).unwrap()
            const out = res.data
            snack.success(`Created Fixation ${out?.fixation_no ?? ''} — opening Allocation`)
            onSaved?.(out?.cs_fixation_id ?? 0)
            onClose()
            // Access `btnSaveAllocate_Click`: chỉ mở Allocation form khi save OK
            // (`only_save = 0`). BE trả `session_uid` đã được seed bằng
            // `choose_fixation_no`, FE chỉ cần mở dialog với fixation tương ứng.
            if (out?.cs_fixation_id && out?.session_uid) {
              onSaveAllocate?.(out)
            }
            return
          }
          const res = await createFix(payload).unwrap()
          snack.success(`Created Fixation ${res.data?.fixation_no ?? ''}`)
          onSaved?.(res.data?.cs_fixation_id ?? 0)
          onClose()
        } catch (err) {
          snack.error(
            getErrorMessage(
              err,
              isEdit
                ? 'Failed to update Fixation'
                : m === 'save-allocate'
                  ? 'Save & Allocate failed'
                  : 'Failed to create Fixation',
            ),
          )
        }
      },
      (errs) => {
        // Schema giờ chỉ ép kiểu (xem comment ở schema): onInvalid chỉ rơi
        // vào đây khi type-shape sai (vd. quantity_kg không phải number) —
        // hiếm gặp. Báo lại field đầu tiên để không phải đoán mò.
        const firstKey = Object.keys(errs)[0]
        const firstMsg = firstKey
          ? ((errs as Record<string, { message?: string }>)[firstKey]?.message ?? `Invalid value for "${firstKey}"`)
          : 'Invalid form value'
        snack.error(firstMsg)
      },
    )

  // Access: contract_no & p_contract_no & pl_index disable trong edit mode.
  const lockNpe = isEdit || !!defaultNpeContractNo
  const lockPContract = isEdit
  // Access: Save & Allocate ẩn khi đã có allocations.
  const showSaveAllocate = !isEdit && allocationsCount === 0

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper">
      <DialogTitle sx={dialogTitleSx}>
        <Typography
          component="span"
          noWrap
          title={isEdit ? 'Edit - NPE Fixation' : 'New - NPE Fixation'}
          sx={dialogTitleTextSx}
        >
          {isEdit ? 'Edit' : 'New'}
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}> - NPE Fixation</Box>
          {isEdit && initialValue?.fixation_no ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.fixation_no}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          {showSaveAllocate && (
            <Button
              variant="contained"
              size="small"
              color="warning"
              onClick={() => void submitWithMode('save-allocate')()}
              disabled={loading || !canEdit}
              sx={dialogHeaderBtnSx}
            >
              {allocating ? <CircularProgress size={14} color="inherit" /> : 'Save & Allocate'}
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => void submitWithMode('save')()}
            disabled={loading || !canEdit}
            sx={dialogHeaderBtnSx}
          >
            {creating || updating ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button variant="outlined" size="small" onClick={onClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {regenNotice ? (
          <Alert severity="warning" sx={{ mb: 1, py: 0.25 }}>
            Contract No. <b>{regenNotice.from}</b> was just created — suggested
            new code <b>{regenNotice.to}</b>.
          </Alert>
        ) : null}
        {/* ========= Section 1: NPEs - Information ========= */}
        <SectionHeader title="NPEs - Information" />
        {isEdit && allocationsCount > 0 && (
          <Alert severity="warning" variant="outlined" sx={{ mt: 0.5, py: 0 }}>
            This Fixation is allocated with {allocationsCount} row
            {allocationsCount > 1 ? 's' : ''}!
          </Alert>
        )}
        <Box component="form" noValidate sx={{ pt: 1 }}>
          <Grid container spacing={1.5}>
            {/* Row: NPE No. (4) | Supplier Name (8) */}
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="npe_no"
                render={({ field }) => {
                  const found = npeOptions.find((c) => c.contract_no === field.value)
                  const selected =
                    found ??
                    (field.value
                      ? ({
                          consignment_p_id: 0,
                          contract_no: field.value,
                          contract_date: null,
                          cp_code: null,
                          quantity_kg: null,
                        } as NPEContractOut)
                      : null)
                  return (
                    <Autocomplete
                      size="small"
                      options={npeOptions}
                      value={selected}
                      onChange={(_e, v) => field.onChange(v?.contract_no ?? '')}
                      getOptionLabel={(opt) => (opt ? opt.contract_no : '')}
                      isOptionEqualToValue={(o, v) => o.contract_no === v.contract_no}
                      disabled={lockNpe}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="NPE No. *"
                          error={!!errors.npe_no}
                          helperText={errors.npe_no?.message}
                          sx={lockNpe ? roSx : undefined}
                        />
                      )}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Supplier Name"
                size="small"
                fullWidth
                value={selectedNpe?.supplier_name ?? selectedNpe?.name ?? ''}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={roSx}
              />
            </Grid>

            {/* Row: NPE - To be Fix (4) — Access btnEdit (subform line 990):
                edit mode = NPE.to_be_fix + cs_fixation.quantity_kg (cộng lại
                quantity hiện tại để hiển thị mức trần cho phép tăng/giảm). */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="NPE - To be Fix"
                size="small"
                fullWidth
                value={(() => {
                  if (selectedNpe?.to_be_fix == null) return ''
                  const offset = isEdit ? (initialValue?.quantity_kg ?? 0) : 0
                  return fmtStandard(selectedNpe.to_be_fix + offset)
                })()}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ ...roSx, ...numSx }}
              />
            </Grid>
            <Grid item xs={12} sm={8} />
          </Grid>

          {/* ========= Section 2: Fixation - Information ========= */}
          <SectionHeader title="Fixation - Information" />
          <Grid container spacing={1.5} sx={{ pt: 1 }}>
            {/* Row: Fixation ID (4) | Annex Index (4) | (empty 4) */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Fixation ID"
                size="small"
                fullWidth
                value={isEdit && initialValue ? String(initialValue.cs_fixation_id) : ''}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={roSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Annex Index"
                size="small"
                fullWidth
                value={annexIndexLabel}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={roSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} />

            {/* Row: Fixation No. (4) | Fixation Date (4) | Note (4) */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Fixation No."
                size="small"
                fullWidth
                value={fixationNoPreview}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={roSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="fixation_date"
                render={({ field }) => (
                  <DatePicker
                    label="Fixation Date *"
                    format="DD-MMM-YYYY"
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(d) => field.onChange(d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        error: !!errors.fixation_date,
                        helperText: errors.fixation_date?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="fixation_note"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Note"
                    size="small"
                    multiline
                    minRows={2}
                    fullWidth
                  />
                )}
              />
            </Grid>

            {/* Row: Fixation Quantity (4) | Fixation Price (4) | (empty 4) */}
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="quantity_kg"
                render={({ field }) => (
                  <NumericField
                    label="Fixation Quantity (KG) *"
                    size="small"
                    fullWidth
                    decimals={0}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={!!errors.quantity_kg}
                    helperText={
                      errors.quantity_kg?.message ??
                      (selectedNpe?.to_be_fix != null
                        ? `≤ ${fmtStandard(
                            selectedNpe.to_be_fix +
                              (isEdit ? (initialValue?.quantity_kg ?? 0) : 0),
                          )} (To be Fix)`
                        : '')
                    }
                    sx={numSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="fixation_price"
                render={({ field }) => (
                  <NumericField
                    label="Fixation Price *"
                    size="small"
                    fullWidth
                    decimals={2}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={!!errors.fixation_price}
                    helperText={errors.fixation_price?.message ?? 'Auto-sync → P Outright Price'}
                    sx={numSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4} />
          </Grid>

          {/* ========= Section 3: Fixation - P Contract ========= */}
          <SectionHeader title="Fixation - P Contract" />
          <Grid container spacing={1.5} sx={{ pt: 1 }}>
            {/* Row: Contract No. | Contract Date | Supplier Code */}
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="contract_no"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Contract No."
                    size="small"
                    fullWidth
                    disabled={lockPContract}
                    placeholder={lockPContract ? '' : 'Auto: P-XXXXX'}
                    InputLabelProps={lockPContract ? { shrink: true } : undefined}
                    onBlur={(e) => {
                      field.onBlur()
                      void handleContractNoBlur()
                      // satisfy react-hook-form
                      void e
                    }}
                    error={!!errors.contract_no}
                    helperText={
                      errors.contract_no?.message ??
                      (precheckBusy
                        ? 'Verifying contract code…'
                        : suggestPContractState.isFetching
                          ? 'Suggesting next code…'
                          : undefined)
                    }
                    sx={lockPContract ? roSx : undefined}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="contract_date"
                render={({ field }) => (
                  <DatePicker
                    label="Contract Date *"
                    format="DD-MMM-YYYY"
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(d) => field.onChange(d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        error: !!errors.contract_date,
                        helperText: errors.contract_date?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="cp_code"
                render={({ field }) => (
                  <Autocomplete
                    size="small"
                    options={vendors}
                    value={selectedVendor ?? (field.value ? { code: field.value, name: '', short_name: null, vat_code: null } : null)}
                    onChange={(_e, v) => field.onChange(v?.code ?? '')}
                    getOptionLabel={(opt) => (opt ? opt.code : '')}
                    isOptionEqualToValue={(o, v) => o.code === v.code}
                    // Access form file: p_cp_code Enabled=False (line 5713) — locked to NPE.cp_code,
                    // auto-fill qua check_money. User không override được.
                    disabled
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Supplier Code *"
                        error={!!errors.cp_code}
                        helperText={errors.cp_code?.message}
                        sx={roSx}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            {/* Row: Supplier Name (full) — Access p_supplier ControlSource:
                `DLookUp("name","public_cp_vn","code='" & [p_cp_code] & "'")`.
                Vì p_cp_code lock theo NPE.cp_code, fallback chain ưu tiên: vendor
                lookup → NPE supplier → fixation view (edit mode). */}
            <Grid item xs={12}>
              <TextField
                label="Supplier Name"
                size="small"
                fullWidth
                value={
                  selectedVendor?.name ??
                  selectedNpe?.supplier_name ??
                  selectedNpe?.name ??
                  initialValue?.name ??
                  ''
                }
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={roSx}
              />
            </Grid>

            {/* Row: Quality Code | Seller's Ref (span 8) */}
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="quality_code"
                render={({ field }) => {
                  const opt = qualities.find((q) => q.code === field.value) ?? null
                  return (
                    <Autocomplete
                      size="small"
                      options={qualities}
                      value={opt ?? (field.value ? { code: field.value, description: '' } : null)}
                      onChange={(_e, v) => field.onChange(v?.code ?? '')}
                      getOptionLabel={(o) => (o ? o.code : '')}
                      isOptionEqualToValue={(o, v) => o.code === v.code}
                      // Access form file: p_quality_code Enabled=False (line 3946) —
                      // auto từ NPE.quality_code, user không sửa.
                      disabled
                      renderInput={(params) => (
                        <TextField {...params} label="Quality Code" sx={roSx} />
                      )}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Controller
                control={control}
                name="seller_ref"
                render={({ field }) => (
                  <TextField {...field} label="Seller's Ref" size="small" fullWidth />
                )}
              />
            </Grid>

            {/* Row: Quantity (Kg) | Outright Price | No of Bags */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Quantity (Kg)"
                size="small"
                fullWidth
                value={typeof currentQty === 'number' ? currentQty.toLocaleString('en-US') : ''}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ ...roSx, ...numSx }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="outright_price"
                render={({ field }) => (
                  <TextField
                    label="Outright Price"
                    size="small"
                    fullWidth
                    value={typeof field.value === 'number' ? fmtStandard(field.value) : ''}
                    InputLabelProps={{ shrink: true }}
                    // Access form: p_outright_price Enabled=False (line 2306) — sync
                    // từ fixation_price (price_AfterUpdate, check_money). Readonly.
                    InputProps={{ readOnly: true }}
                    error={!!errors.outright_price}
                    helperText={errors.outright_price?.message}
                    sx={{ ...numSx, ...roSx }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="no_of_bag"
                render={({ field }) => (
                  <NumericField
                    label="No of Bags"
                    size="small"
                    fullWidth
                    decimals={0}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={!!errors.no_of_bag}
                    helperText={errors.no_of_bag?.message}
                    sx={numSx}
                  />
                )}
              />
            </Grid>

            {/* Row: Certification | ME or IB | (empty) */}
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="certification"
                render={({ field }) => {
                  // Đảm bảo giá trị auto-fill từ NPE LUÔN hiển thị: certification
                  // master có thể không chứa value đó (đã inactive / lệch case)
                  // → field hiện blank dù state đã set. Inject value hiện tại vào
                  // options (mirror pattern arbitration / lot-mgt contract combo).
                  const certOptions =
                    field.value && !certifications.some((c) => c.certification === field.value)
                      ? [{ certification: field.value }, ...certifications]
                      : certifications
                  const opt = certOptions.find((c) => c.certification === field.value) ?? null
                  return (
                    <Autocomplete
                      size="small"
                      options={certOptions}
                      value={opt}
                      onChange={(_e, v) => field.onChange(v?.certification ?? '')}
                      getOptionLabel={(o) => (o ? o.certification : '')}
                      isOptionEqualToValue={(o, v) => o.certification === v.certification}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Certification"
                          error={!!errors.certification}
                          helperText={errors.certification?.message}
                        />
                      )}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="me_or_ib"
                render={({ field }) => {
                  const sel = meOrIbOpts.find((o) => o.value === field.value)
                    ?? (field.value ? { value: field.value as 'ME' | 'IB', label: '' } : null)
                  return (
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={meOrIbOpts}
                      value={sel}
                      onChange={(_e, v) => field.onChange(v?.value ?? '')}
                      getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                      isOptionEqualToValue={(a, b) => a.value === b.value}
                      renderInput={(params) => <TextField {...params} label="ME or IB" />}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} />

            {/* Row: License Cert No. | License SI | License SD */}
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="license_cert_no"
                render={({ field }) => {
                  const opt = licenses.find((l) => l.license_cert_no === field.value) ?? null
                  return (
                    <Autocomplete
                      size="small"
                      options={licenses}
                      value={opt}
                      onChange={(_e, v) => field.onChange(v?.license_cert_no ?? '')}
                      getOptionLabel={(o) => (o ? o.license_cert_no : '')}
                      isOptionEqualToValue={(o, v) => o.license_cert_no === v.license_cert_no}
                      renderInput={(params) => <TextField {...params} label="License Cert No." />}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="License - SI (VND/KG)"
                size="small"
                fullWidth
                value={siValue === '' || siValue == null ? '' : Number(siValue).toLocaleString('en-US')}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ ...roSx, ...numSx }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="License - SD (VND/KG)"
                size="small"
                fullWidth
                value={sdValue === '' || sdValue == null ? '' : Number(sdValue).toLocaleString('en-US')}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ ...roSx, ...numSx }}
              />
            </Grid>

            {/* Row: Package Code | Packaging (span 8 read-only) */}
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="package"
                render={({ field }) => {
                  const sel = packages.find((p) => p.value === field.value)
                    ?? (field.value ? { value: field.value, label: '' } : null)
                  return (
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={packages}
                      value={sel}
                      onChange={(_e, v) => field.onChange(v?.value ?? '')}
                      getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                      isOptionEqualToValue={(a, b) => a.value === b.value}
                      renderInput={(params) => (
                        <TextField {...params} label="Package Code"
                          error={!!errors.package} helperText={errors.package?.message} />
                      )}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Packaging"
                size="small"
                fullWidth
                value={selectedPackage?.label ?? ''}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={roSx}
              />
            </Grid>

            {/* Row: Contract Type | Applicable VAT | VAT Rate */}
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="contract_type"
                render={({ field }) => {
                  // Access form: p_contract_type Enabled=False (line 2217) — luôn
                  // "Outright" per check_money (NPE Fixation = chốt giá outright).
                  const opts: { value: 'Outright' | 'Differential' }[] = [{ value: 'Outright' }, { value: 'Differential' }]
                  const sel = opts.find((o) => o.value === field.value) ?? null
                  return (
                    <Autocomplete
                      size="small"
                      fullWidth
                      disabled
                      options={opts}
                      value={sel}
                      onChange={(_e, v) => { if (v?.value) field.onChange(v.value) }}
                      getOptionLabel={(o) => o ? o.value : ''}
                      isOptionEqualToValue={(a, b) => a.value === b.value}
                      renderInput={(params) => <TextField {...params} label="Contract Type" sx={roSx} />}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="applicable_vat"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Applicable VAT?"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="vat_rate"
                render={({ field }) => (
                  <NumericField
                    label="VAT Rate"
                    size="small"
                    fullWidth
                    disabled={!applicableVat}
                    percent
                    decimals={2}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={!!errors.vat_rate}
                    helperText={errors.vat_rate?.message}
                    sx={numSx}
                  />
                )}
              />
            </Grid>

            {/* Row: Price Unit | Futures Month | Fixation Call */}
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="price_unit"
                render={({ field }) => {
                  const sel = priceUnits.find((o) => o.value === field.value)
                    ?? (field.value ? { value: field.value, label: '' } : null)
                  return (
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={priceUnits}
                      value={sel}
                      onChange={(_e, v) => field.onChange(v?.value ?? '')}
                      getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                      isOptionEqualToValue={(a, b) => a.value === b.value}
                      renderInput={(params) => (
                        <TextField {...params} label="Price Unit"
                          error={!!errors.price_unit} helperText={errors.price_unit?.message} />
                      )}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="futures_month"
                render={({ field }) => {
                  const sel = futuresMonths.find((o) => o.value === field.value)
                    ?? (field.value ? { value: field.value, label: '' } : null)
                  return (
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={futuresMonths}
                      value={sel}
                      onChange={(_e, v) => field.onChange(v?.value ?? '')}
                      getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                      isOptionEqualToValue={(a, b) => a.value === b.value}
                      renderInput={(params) => <TextField {...params} label="Futures Month" />}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="fixation_call"
                render={({ field }) => {
                  const sel = fixationCalls.find((o) => o.value === field.value)
                    ?? (field.value ? { value: field.value, label: '' } : null)
                  return (
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={fixationCalls}
                      value={sel}
                      onChange={(_e, v) => field.onChange(v?.value ?? '')}
                      getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                      isOptionEqualToValue={(a, b) => a.value === b.value}
                      renderInput={(params) => <TextField {...params} label="Fixation Call" />}
                    />
                  )
                }}
              />
            </Grid>

            {/* Row: FX | Logistic Cost | (empty) */}
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="fx"
                render={({ field }) => (
                  <NumericField
                    label="FX"
                    size="small"
                    fullWidth
                    decimals={0}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    sx={numSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="logistic_cost"
                render={({ field }) => (
                  <NumericField
                    label="Logistic Cost"
                    size="small"
                    fullWidth
                    decimals={2}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    sx={numSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4} />

            {/* Row: Latest Delivery | Delivery Term | (empty) */}
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="latest_delivery"
                render={({ field }) => (
                  <DatePicker
                    label="Lasted Delivery"
                    format="DD-MMM-YYYY"
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(d) => field.onChange(d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="delivery_term"
                render={({ field }) => {
                  const sel = deliveryTerms.find((o) => o.value === field.value)
                    ?? (field.value ? { value: field.value, label: '' } : null)
                  return (
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={deliveryTerms}
                      value={sel}
                      onChange={(_e, v) => field.onChange(v?.value ?? '')}
                      getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                      isOptionEqualToValue={(a, b) => a.value === b.value}
                      renderInput={(params) => (
                        <TextField {...params} label="Delivery Term"
                          error={!!errors.delivery_term} helperText={errors.delivery_term?.message} />
                      )}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} />

            {/* Row: Delivery Place | Delivery Place Name (span 8) */}
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="delivery_place_code"
                render={({ field }) => {
                  const sel = deliveryPlaces.find((o) => o.value === field.value)
                    ?? (field.value ? { value: field.value, label: '' } : null)
                  return (
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={deliveryPlaces}
                      value={sel}
                      onChange={(_e, v) => field.onChange(v?.value ?? '')}
                      getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                      isOptionEqualToValue={(a, b) => a.value === b.value}
                      renderInput={(params) => (
                        <TextField {...params} label="Delivery Place"
                          error={!!errors.delivery_place_code} helperText={errors.delivery_place_code?.message} />
                      )}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Delivery Place Name"
                size="small"
                fullWidth
                value={selectedDeliveryPlace?.label ?? ''}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={roSx}
              />
            </Grid>

            {/* Row: Note (full) */}
            <Grid item xs={12}>
              <Controller
                control={control}
                name="note"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Note"
                    size="small"
                    multiline
                    minRows={2}
                    fullWidth
                  />
                )}
              />
            </Grid>

            {/* Row: Trader In Charge | PIC */}
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="trader_in_charge"
                render={({ field }) => {
                  const opt = traders.find((t) => t.username === field.value) ?? null
                  return (
                    <Autocomplete
                      size="small"
                      options={traders}
                      value={opt ?? (field.value ? { username: field.value, full_name: '' } : null)}
                      onChange={(_e, v) => field.onChange(v?.username ?? '')}
                      getOptionLabel={(o) => (o ? o.full_name || o.username : '')}
                      isOptionEqualToValue={(o, v) => o.username === v.username}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Trader In Charge"
                          error={!!errors.trader_in_charge}
                          helperText={errors.trader_in_charge?.message}
                        />
                      )}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="PIC"
                size="small"
                fullWidth
                value={traders.find((t) => t.username === traderInCharge)?.full_name ?? ''}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={roSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} />

            {/* Row: Fixed FX | Provi. Market rate | Stoploss rate */}
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="fixed_fx"
                render={({ field }) => (
                  <NumericField
                    label="Fixed FX (If have)"
                    size="small"
                    fullWidth
                    decimals={0}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    sx={numSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="provi_market_rate"
                render={({ field }) => (
                  <NumericField
                    label="Provi. Market rate"
                    size="small"
                    fullWidth
                    // Access form: provi_market_rate Format="Percent" (line 6543).
                    percent
                    decimals={2}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={!!errors.provi_market_rate}
                    helperText={errors.provi_market_rate?.message}
                    sx={numSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="stoploss_rate"
                render={({ field }) => (
                  <NumericField
                    label="Stoploss rate"
                    size="small"
                    fullWidth
                    // Access form: stoploss_rate Format="Percent" (line 6630).
                    percent
                    decimals={2}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={!!errors.stoploss_rate}
                    helperText={errors.stoploss_rate?.message}
                    sx={numSx}
                  />
                )}
              />
            </Grid>

            {/* Row: Flags */}
            <Grid item xs={12}>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2}>
                <Controller
                  control={control}
                  name="is_intermediary"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Intermediary"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="is_issued"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Issued"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="is_sent_mail"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Sent mail"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="is_received_mail"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Received mail"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="is_sent_paper"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Sent paper"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="is_received_paper"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Received paper"
                    />
                  )}
                />
              </Stack>
            </Grid>

            {/* Row: Shipper Code | Shipper Name (span 8) — luôn enable
                (Access form không lock theo Intermediary checkbox). */}
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="shipper_code"
                render={({ field }) => (
                  <Autocomplete
                    size="small"
                    options={vendors}
                    value={
                      selectedShipper ??
                      (field.value
                        ? { code: field.value, name: '', short_name: null, vat_code: null }
                        : null)
                    }
                    onChange={(_e, v) => field.onChange(v?.code ?? '')}
                    getOptionLabel={(o) => (o ? o.code : '')}
                    isOptionEqualToValue={(o, v) => o.code === v.code}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Shipper Code"
                        error={!!errors.shipper_code}
                        helperText={errors.shipper_code?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Shipper Name"
                size="small"
                fullWidth
                value={selectedShipper?.name ?? ''}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={roSx}
              />
            </Grid>

            {/* Row: Contract Ref | Outright Price (shipper) | PTBF Diffs (shipper) */}
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="contract_shipper_intermediary"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Contract Ref"
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="outright_shipper_intermediary"
                render={({ field }) => (
                  <NumericField
                    label="Outright Price"
                    size="small"
                    fullWidth
                    decimals={2}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    sx={numSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="ptbfdiffs_shipper_intermediary"
                render={({ field }) => (
                  <NumericField
                    label="PTBF Diffs"
                    size="small"
                    fullWidth
                    decimals={2}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    sx={numSx}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default NPEFixationFormDialog
